import { call, put, select, takeLatest } from "redux-saga/effects";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import {
  AnyEntity,
  EntityType,
  EntityTypesSupportedByMapsPage,
  MapEntity,
} from "@mapmycustomers/shared/types/entity";
import { MapEntry } from "@mapmycustomers/shared/types/map";
import Organization from "@mapmycustomers/shared/types/Organization";
import { OrganizationSize } from "@mapmycustomers/shared/types/OrganizationSize";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";
import PlatformFilterModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import PlatformMapRequest from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformMapRequest";

import localSettings from "@app/config/LocalSettings";
import ViewMode from "@app/enum/dashboard/ViewMode";
import { NO_CONTACT_IN_ENTITY_TYPES } from "@app/scene/dashboard/components/cards/NoContactIn/consts";
import {
  getNoContactInDrillDownTotalFilteredRecords,
  getNoContactInDrillDownViewStates,
} from "@app/scene/dashboard/store/cards/noContactIn/selectors";
import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import { ApiMethodName } from "@app/store/api/ApiService";
import { callApi } from "@app/store/api/callApi";
import { fetchNoContactInCountCardDataHelper } from "@app/store/dashboard/cardDataFetchHelpers";
import { handleError } from "@app/store/errors/actions";
import { exportEntities } from "@app/store/exportEntities/actions";
import { getOrganizationId, getOrganizationSize } from "@app/store/iam";
import { getMapViewSettings } from "@app/store/map";
import convertMapFilterModelToFilterModelForEntity from "@app/store/savedFilters/convertMapFilterModelToFilterModel";
import EntityTypesSupportedInNoContactIn from "@app/types/dashboard/cards/noContactIn/EntityTypesSupportedInNoContactIn";
import FrequencyStatusType from "@app/types/dashboard/FrequencyStatusType";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import { CompanyFieldName } from "@app/util/fieldModel/CompanyFieldModel";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import FieldModel from "@app/util/fieldModel/impl/FieldModel";
import { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";
import {
  createAndCondition,
  createEmptyFilterModel,
  createSimpleCondition,
} from "@app/util/filters/utils";
import loggingService from "@app/util/logging";
import categorizeMapEntries from "@app/util/map/categorizeMapEnties";
import getPrecisionDetails from "@app/util/map/getPrecisionDetails";
import { takeLatestBy } from "@app/util/redux/takeLatestBy";
import convertMapPlatformFilterToMapFilterModel from "@app/util/viewModel/convertMapPlatformFilterToMapFilterModel";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import getDefaultDrillDownColumns from "../../utils/getDefaultDrillDownColumns";

import {
  applyNoContactInDrillDownListViewSettings,
  exportNoContactInCardDrillDownData,
  fetchNoContactInCardData,
  fetchNoContactInCardDrillDownData,
  fetchNoContactInCountCardData,
  showNoContactInDrillDown,
} from "./actions";

const LIST_PAGE_SIZE = 20;

const defaultColumns: Record<EntityTypesSupportedInNoContactIn, string[]> = {
  [EntityType.COMPANY]: [
    CompanyFieldName.NAME,
    CompanyFieldName.USER,
    CompanyFieldName.ADDRESS,
    CompanyFieldName.NO_CONTACT_IN,
    CompanyFieldName.SOURCE,
  ],
  [EntityType.DEAL]: [
    DealFieldName.NAME,
    DealFieldName.USER,
    DealFieldName.ADDRESS,
    DealFieldName.NO_CONTACT_IN,
    DealFieldName.SOURCE,
  ],
  [EntityType.PERSON]: [
    PersonFieldName.NAME,
    PersonFieldName.USER,
    PersonFieldName.ADDRESS,
    PersonFieldName.NO_CONTACT_IN,
    PersonFieldName.SOURCE,
  ],
};

const defaultSortColumn: Record<EntityTypesSupportedInNoContactIn, string> = {
  [EntityType.COMPANY]: CompanyFieldName.LAST_ACTIVITY_DATE,
  [EntityType.DEAL]: DealFieldName.LAST_ACTIVITY_DATE,
  [EntityType.PERSON]: PersonFieldName.LAST_ACTIVITY_DATE,
};

const fetchMethodByEntityType: Record<EntityTypesSupportedInNoContactIn, ApiMethodName> = {
  [EntityType.COMPANY]: "fetchCompanies",
  [EntityType.DEAL]: "fetchDeals",
  [EntityType.PERSON]: "fetchPeople",
};

function* onFetchNoContactInCardData({ payload }: ReturnType<typeof fetchNoContactInCardData>) {
  const { callback, configuration, failureCallback, offset, scope, viewMode, viewport } = payload;
  const { criteria, dateRange, extraFilters } = configuration;
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const mapViewState: MapViewState = yield select(getMapViewSettings);

    const organizationSize: OrganizationSize = yield select(getOrganizationSize);

    const entityTypes =
      Array.isArray(criteria.entityTypes) && criteria.entityTypes.length
        ? criteria.entityTypes
        : NO_CONTACT_IN_ENTITY_TYPES;

    const payload: PlatformMapRequest = {
      $filters: {
        $and: [
          { noContactDaysOut: dateRange.days === null ? null : { $gt: dateRange.days } },
          ...(!scope?.userId && scope?.teamId ? [{ teamId: { $in: [scope.teamId] } }] : []),
          ...(scope?.userId ? [{ userId: { $in: [scope.userId] } }] : []),
          ...(criteria.sources?.length ? [{ source: { $in: criteria.sources } }] : []),
          ...(criteria.frequencyStatus && criteria.frequencyStatus.length === 1
            ? [
                {
                  cadenceStatus: {
                    [criteria.frequencyStatus[0] === FrequencyStatusType.UNKNOWN ? "$eq" : "$ne"]:
                      FrequencyStatusType.UNKNOWN,
                  },
                },
              ]
            : []),
          ...(extraFilters?.$and ?? []),
          ...(extraFilters?.$or ? [{ $or: extraFilters.$or }] : []),
        ],
        cadence: true,
        entities: entityTypes.reduce(
          (result, entityType) => ({
            ...result,
            [entityType]: {
              ...((
                extraFilters?.entities as
                  | Partial<Record<EntityTypesSupportedByMapsPage, PlatformFilterModel>>
                  | undefined
              )?.[entityType] ?? {}),
              includeCustomFields: true,
              includeGroups: true,
              includeNotes: true,
              includeRoutes: true,
              includeTerritories: true,
            },
          }),
          {}
        ),
        includeCustomFields: true,
        pinLegends: entityTypes.reduce(
          (result, entityType) => ({
            ...result,
            [entityType]: {
              color: mapViewState.colorKey![entityType],
              shape: mapViewState.shapeKey![entityType],
            },
          }),
          {}
        ),
        ...(viewMode === ViewMode.MAP
          ? {
              bounds: viewport?.bounds,
              ...getPrecisionDetails(viewport?.zoom ?? 1, organizationSize),
            }
          : {}),
      },
      $limit: viewMode === ViewMode.LIST ? LIST_PAGE_SIZE : undefined,
      $offset: offset,
      $order: "-noContactDaysOut",
    };

    if (viewMode === ViewMode.MAP) {
      const response: ListResponse<MapEntry> = yield callApi("fetchMapPins", orgId, payload);
      yield call(callback, {
        categorizedMapEntries: categorizeMapEntries(response.data),
        total: response.total,
      });
    } else {
      const response: ListResponse<MapEntity> = yield callApi("fetchPins", orgId, payload);
      yield call(callback, { entities: response.data, total: response.total });
    }
  } catch (error) {
    yield put(handleError({ error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

function* onFetchNoContactInCountCardData({
  payload,
}: ReturnType<typeof fetchNoContactInCountCardData>) {
  const { callback, configuration, failureCallback, scope } = payload;
  try {
    yield call(fetchNoContactInCountCardDataHelper, { callback, configuration, scope });
  } catch (error) {
    yield put(handleError({ error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

function* onOpenDrillDown({ payload }: ReturnType<typeof showNoContactInDrillDown.request>) {
  const { configuration, scope } = payload;
  const { criteria, extraFilters } = configuration;

  const viewStates: Record<EntityTypesSupportedInNoContactIn, DrillDownViewState> = yield select(
    getNoContactInDrillDownViewStates
  );

  const entityTypes =
    Array.isArray(criteria.entityTypes) && criteria.entityTypes.length
      ? criteria.entityTypes
      : NO_CONTACT_IN_ENTITY_TYPES;

  entityTypes.forEach((entityType) => {
    const fieldModel = getFieldModelByEntityType(entityType);
    const viewState = localSettings.getViewSettings(
      `dashboard/noContactIn/${entityType}`,
      fieldModel,
      {
        ...viewStates[entityType],
        columns: getDefaultDrillDownColumns(defaultColumns[entityType], fieldModel),
        sort: [
          {
            field: fieldModel.getByName(defaultSortColumn[entityType])!,
            order: SortOrder.DESC,
          },
        ],
      }
    );
    viewStates[entityType] = viewState;
  });

  const mapFilterModel = convertMapPlatformFilterToMapFilterModel(extraFilters ?? {});

  entityTypes.forEach((entityType) => {
    const viewState: DrillDownViewState = viewStates[entityType];

    const isTeamScope = !!scope?.teamId && !scope?.userId;

    const filter: FilterModel = createAndCondition([
      convertMapFilterModelToFilterModelForEntity(entityType, mapFilterModel),
      configuration.dateRange.days === null
        ? createSimpleCondition(CommonFieldName.NO_CONTACT_IN, FilterOperator.EMPTY)
        : createSimpleCondition(
            CommonFieldName.NO_CONTACT_IN,
            FilterOperator.GREATER_THAN,
            configuration.dateRange.days
          ),
    ]);
    if (isTeamScope) {
      filter.conditions.push(
        createSimpleCondition(CommonFieldName.TEAM, FilterOperator.IN_ANY, [scope.teamId!])
      );
    }
    if (scope?.userId) {
      filter.conditions.push(
        createSimpleCondition(CommonFieldName.USER, FilterOperator.IN_ANY, [scope.userId])
      );
    }
    if (criteria.sources.length) {
      filter.conditions.push(
        createSimpleCondition(CommonFieldName.SOURCE, FilterOperator.IN_ANY, criteria.sources)
      );
    }
    viewState.filter = filter;

    viewStates[entityType] = viewState;
  });

  yield put(showNoContactInDrillDown.success({ viewStates }));
}

export function* onFetchDrillDownData({
  payload,
}: ReturnType<typeof fetchNoContactInCardDrillDownData.request>) {
  try {
    loggingService.debug("Dashboard: no contact in card, onFetchDrillDownData", payload);
    if (!payload.updateOnly) {
      // We do not listen to filter returned by AgGrid from PlatformDataSource
      delete payload.request.filter;
    }

    if (!payload.fetchOnlyWithoutFilters) {
      yield put(
        applyNoContactInDrillDownListViewSettings({
          ...payload.request,
          entityType: payload.entityType,
        })
      );
    }

    const drillDownViewStates: Record<EntityTypesSupportedInNoContactIn, DrillDownViewState> =
      yield select(getNoContactInDrillDownViewStates);
    const drillDownViewState = drillDownViewStates[payload.entityType];

    if (!payload.fetchOnlyWithoutFilters) {
      localSettings.setViewSettings(
        drillDownViewState,
        `dashboard/noContactIn/${payload.entityType}`
      );
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.startRow
        : drillDownViewState.range.startRow;
    const $limit =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.endRow - payload.request.range.startRow
        : drillDownViewState.range.endRow - drillDownViewState.range.startRow;

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const fieldModel: FieldModel = yield call(getFieldModelByEntityType, payload.entityType!);
    const requestPayload = {
      $filters: {
        includeAccessStatus: true,
        ...convertToPlatformFilterModel(
          payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : drillDownViewState.filter,
          drillDownViewState.columns,
          fieldModel
        ),
      },
      $limit,
      $offset,
      $order: convertToPlatformSortModel(drillDownViewState.sort),
    };

    const fetchMethod = fetchMethodByEntityType[payload.entityType];
    const response: ListResponse<AnyEntity> = yield callApi(fetchMethod, orgId, requestPayload);
    if (payload.dataCallback) {
      yield call(payload.dataCallback, response);
    }
    yield put(
      fetchNoContactInCardDrillDownData.success({
        entityType: payload.entityType,
        totalFilteredRecords: response.total,
        totalRecords: response.accessible,
      })
    );
  } catch (error) {
    payload.failCallback?.();
    yield put(fetchNoContactInCardDrillDownData.failure(error));
    yield put(handleError({ error }));
  }
}

function* onExport({ payload }: ReturnType<typeof exportNoContactInCardDrillDownData>) {
  const drillDownViewStates: Record<EntityTypesSupportedInNoContactIn, DrillDownViewState> =
    yield select(getNoContactInDrillDownViewStates);
  const total: Record<EntityTypesSupportedInNoContactIn, number> = yield select(
    getNoContactInDrillDownTotalFilteredRecords
  );

  yield put(
    exportEntities.request({
      entityType: payload.entityType,
      total: total[payload.entityType],
      viewState: drillDownViewStates[payload.entityType],
    })
  );
}

export function* noContactInSagas() {
  // we don't use takeLatest because there might be several cards of such type on board
  // that's why we use takeLatestBy and filter by card id
  yield takeLatestBy(
    fetchNoContactInCardData,
    onFetchNoContactInCardData,
    (action: ReturnType<typeof fetchNoContactInCardData>) => action.payload.cardId
  );
  yield takeLatestBy(
    fetchNoContactInCountCardData,
    onFetchNoContactInCountCardData,
    (action: ReturnType<typeof fetchNoContactInCountCardData>) => action.payload.cardId
  );

  yield takeLatest(showNoContactInDrillDown.request, onOpenDrillDown);
  yield takeLatest(fetchNoContactInCardDrillDownData.request, onFetchDrillDownData);
  yield takeLatest(exportNoContactInCardDrillDownData, onExport);
}
